import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { ScanLine, Grid3x3, Coins, Check, Loader2, Plus, Network } from "lucide-react";
import { toast } from "sonner";
import { api } from "../lib/api";
import { useAuth } from "../lib/auth";
import CardRewardModal from "./CardRewardModal";

/**
 * ArtifactExtras — cross-cutting "tab extension" features that attach to every
 * artifact (curated dossier, AI-Compiled dive, AI connection):
 *
 *   • G — Negative-Space Scanner: surfaces high-mention entities the
 *     artifact talks about but didn't make explicit subjects. Multi-select +
 *     "Investigate as Connection" wires them up on Connect-the-Dots in one click.
 *   • J — Artifact Card mint: spend 50 coins to mint a one-of-a-kind
 *     personal Artifact Card. Cards land in your Vault Cards collection.
 */
export default function ArtifactExtras({ kind, ref: artifactRef, subjects = [] }) {
  return (
    <div className="space-y-8 mt-12" data-testid="artifact-extras">
      <NegativeSpace kind={kind} artifactRef={artifactRef} subjects={subjects} />
      <CategoryHeatmap kind={kind} artifactRef={artifactRef} />
      <MintArtifactCard kind={kind} artifactRef={artifactRef} />
    </div>
  );
}

// ============================================================
// G — Negative-Space Scanner (multi-select)
// ============================================================
function NegativeSpace({ kind, artifactRef, subjects }) {
  const nav = useNavigate();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  // Multi-select state: { [entityName]: true } — pick any combo of suggestions
  // (plus optionally the artifact's own subjects, which are always included)
  // and spin them up on Connect-the-Dots.
  const [picked, setPicked] = useState({});

  useEffect(() => {
    let alive = true;
    api.get(`/artifact/${kind}/${encodeURIComponent(artifactRef)}/negative-space?limit=8`)
      .then((r) => { if (alive) setData(r.data); })
      .catch(() => { if (alive) setData({ suggestions: [] }); })
      .finally(() => { if (alive) setLoading(false); });
    return () => { alive = false; };
  }, [kind, artifactRef]);

  const toggle = (name) => {
    setPicked((prev) => {
      const next = { ...prev };
      if (next[name]) delete next[name];
      else next[name] = true;
      return next;
    });
  };

  const pickedNames = Object.keys(picked);
  const investigate = () => {
    if (pickedNames.length < 1) {
      toast.error("Pick at least one entity to investigate");
      return;
    }
    // Always pair the picks with the artifact's existing subjects so the
    // analyst has the original anchor + the new entities to chase. Cap at 12.
    const items = [...subjects.filter(Boolean), ...pickedNames].slice(0, 12);
    if (items.length < 2) {
      toast.error("Need 2+ subjects total — pick one more entity");
      return;
    }
    try {
      sessionStorage.setItem("vault.connect.prefill", JSON.stringify({
        items,
        context: `from negative-space on ${data?.title || kind}`,
      }));
    } catch { /* ignore */ }
    nav("/connect-dots");
  };

  if (loading) {
    return (
      <Card title="Negative-Space Scanner" subtitle="Entities this file talks about but didn't make explicit subjects." icon={<ScanLine size={11} />} accent="#ffd60a" testid="neg-space-card">
        <div className="font-mono text-[10px] text-zinc-500 py-3 flex items-center gap-2">
          <Loader2 size={11} className="animate-spin" /> SCANNING…
        </div>
      </Card>
    );
  }
  if (!data?.suggestions?.length) {
    return null;
  }
  return (
    <Card
      title="Negative-Space Scanner"
      subtitle={
        <>
          Names this file mentions repeatedly but never made an explicit subject.
          Click any chips to select multiple, then hit <strong className="text-[#22d3ee]">Investigate as Connection</strong>{" "}
          to spin them up on Connect-the-Dots together with the file's existing subjects.
        </>
      }
      icon={<ScanLine size={11} />}
      accent="#ffd60a"
      testid="neg-space-card"
    >
      <div className="flex items-center justify-between mb-3 flex-wrap gap-2">
        <span className="font-mono text-[10px] tracking-[0.2em] uppercase text-zinc-500">
          {pickedNames.length} of {data.suggestions.length} selected
        </span>
        <div className="flex items-center gap-2">
          {pickedNames.length > 0 && (
            <button
              type="button"
              onClick={() => setPicked({})}
              data-testid="neg-space-clear"
              className="font-mono text-[10px] tracking-[0.2em] uppercase text-zinc-500 hover:text-white"
            >CLEAR</button>
          )}
          <button
            type="button"
            onClick={investigate}
            disabled={pickedNames.length < 1}
            data-testid="neg-space-investigate"
            className="inline-flex items-center gap-2 px-4 py-2 font-mono text-[10px] tracking-[0.2em] uppercase bg-[#22d3ee] text-[#050505] hover:bg-white disabled:opacity-30 disabled:cursor-not-allowed transition-colors"
          >
            <Network size={11} /> INVESTIGATE AS CONNECTION
            {pickedNames.length >= 1 ? <span>· {pickedNames.length}</span> : null}
          </button>
        </div>
      </div>
      <div className="flex flex-wrap gap-2">
        {data.suggestions.map((s) => {
          const isPicked = !!picked[s.name];
          return (
            <button
              key={s.name}
              type="button"
              onClick={() => toggle(s.name)}
              data-testid={`neg-space-${s.name}`}
              aria-pressed={isPicked}
              className={`inline-flex items-center gap-2 px-3 py-2 border font-mono text-[11px] transition-colors ${
                isPicked
                  ? "bg-[#ffd60a]/20 border-[#ffd60a] text-[#ffd60a]"
                  : "border-[#ffd60a]/40 text-[#ffd60a] hover:bg-[#ffd60a]/10"
              }`}
            >
              {isPicked ? <Check size={11} /> : <Plus size={11} />} {s.name}
              <span className={`text-[9px] ${isPicked ? "text-[#ffd60a]/80" : "text-[#ffd60a]/50"}`}>×{s.mentions}</span>
            </button>
          );
        })}
      </div>
    </Card>
  );
}


// ============================================================
// M — Category Heatmap (categories × kinds, 3 sub-columns per category)
// ============================================================
function CategoryHeatmap({ kind, artifactRef }) {
  const nav = useNavigate();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  // Row multi-select — pick entities and spin them on Connect-the-Dots.
  const [picked, setPicked] = useState({});

  useEffect(() => {
    let alive = true;
    api.get(`/artifact/${kind}/${encodeURIComponent(artifactRef)}/category-heatmap?top_entities=6`)
      .then((r) => { if (alive) setData(r.data); })
      .catch(() => { if (alive) setData(null); })
      .finally(() => { if (alive) setLoading(false); });
    return () => { alive = false; };
  }, [kind, artifactRef]);

  if (loading) {
    return (
      <Card title="Entity × Category Heatmap" subtitle="Where else this file's subjects show up across the archive." icon={<Grid3x3 size={11} />} accent="#22d3ee" testid="heatmap-card">
        <div className="font-mono text-[10px] text-zinc-500 py-3 flex items-center gap-2">
          <Loader2 size={11} className="animate-spin" /> SCANNING…
        </div>
      </Card>
    );
  }
  if (!data?.rows?.length || !data?.cols?.length) return null;

  // Compute max for color intensity (per matrix cell)
  let max = 1;
  for (const row of data.matrix) for (const v of row) if (v > max) max = v;
  const intensity = (v) => v <= 0 ? 0 : Math.min(1, 0.18 + (v / max) * 0.82);

  const onCellClick = async (entity, _col, v) => {
    if (!v) return;
    try {
      // No kind preference — they're all the same thing. Backend picks the
      // best match by title-strength.
      const r = await api.get(`/artifact/entity-lookup`, { params: { q: entity } });
      if (r.data?.found && r.data.url) {
        nav(r.data.url);
        return;
      }
    } catch { /* fall through */ }
    nav(`/deep-dive?topic=${encodeURIComponent(entity)}`);
  };

  const togglePick = (entity) => {
    setPicked((prev) => {
      const next = { ...prev };
      if (next[entity]) delete next[entity];
      else next[entity] = true;
      return next;
    });
  };

  const pickedNames = Object.keys(picked);
  const investigate = () => {
    if (pickedNames.length < 2) {
      toast.error("Pick at least 2 entities to wire up a connection");
      return;
    }
    try {
      sessionStorage.setItem("vault.connect.prefill", JSON.stringify({
        items: pickedNames.slice(0, 12),
        context: `from heatmap on ${data.title || kind}`,
      }));
    } catch { /* ignore */ }
    nav("/connect-dots");
  };

  return (
    <Card
      title="Entity × Category Heatmap"
      subtitle={
        <>
          For every named entity in this file, each cell shows how many other
          files in that category mention it — files of all kinds counted
          together. <strong className="text-white">Click a hot cell</strong> to
          jump to a matching file (or, if the entity has never been
          investigated, into a fresh Deep Dive pre-filled with it). Tick row
          checkboxes then hit <strong className="text-[#22d3ee]">Investigate as
          Connection</strong> to spin up multiple entities on Connect-the-Dots.
        </>
      }
      icon={<Grid3x3 size={11} />}
      accent="#22d3ee"
      testid="heatmap-card"
    >
      <div className="flex items-center justify-between mb-3 flex-wrap gap-2">
        <span className="font-mono text-[10px] tracking-[0.2em] uppercase text-zinc-500">
          {pickedNames.length} of {data.rows.length} selected
        </span>
        <div className="flex items-center gap-2">
          {pickedNames.length > 0 && (
            <button
              type="button"
              onClick={() => setPicked({})}
              data-testid="heatmap-clear-picks"
              className="font-mono text-[10px] tracking-[0.2em] uppercase text-zinc-500 hover:text-white"
            >CLEAR</button>
          )}
          <button
            type="button"
            onClick={investigate}
            disabled={pickedNames.length < 2}
            data-testid="heatmap-investigate"
            className="inline-flex items-center gap-2 px-4 py-2 font-mono text-[10px] tracking-[0.2em] uppercase bg-[#22d3ee] text-[#050505] hover:bg-white disabled:opacity-30 disabled:cursor-not-allowed transition-colors"
          >
            <Network size={11} /> INVESTIGATE AS CONNECTION
            {pickedNames.length >= 2 ? <span>· {pickedNames.length}</span> : null}
          </button>
        </div>
      </div>
      <div className="overflow-x-auto -mx-2 px-2">
        <table className="min-w-full text-[10px] font-mono" data-testid="heatmap-table">
          <thead>
            <tr>
              <th className="sticky left-0 bg-[#0a0a0a] text-left text-zinc-500 pr-3 pb-1.5 uppercase tracking-[0.15em] align-bottom">
                <span className="inline-flex items-center gap-2"><span className="text-zinc-700">☐</span> Entity</span>
              </th>
              {data.cols.map((c) => (
                <th key={c.id} className="px-1.5 pb-1 text-[#22d3ee]/70 align-bottom whitespace-nowrap">
                  <div className="rotate-180 [writing-mode:vertical-rl] py-1 uppercase tracking-[0.12em]" style={{ minHeight: 96 }}>
                    {c.label}
                  </div>
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {data.rows.map((entity, ri) => {
              const isPicked = !!picked[entity];
              return (
                <tr key={entity} className={`border-t border-white/5 ${isPicked ? "bg-[#22d3ee]/[0.06]" : ""}`}>
                  <td className="sticky left-0 bg-[#0a0a0a] pr-3 py-1.5 text-white whitespace-nowrap">
                    <label className="inline-flex items-center gap-2 cursor-pointer select-none" data-testid={`heatmap-row-pick-${ri}`}>
                      <input
                        type="checkbox"
                        checked={isPicked}
                        onChange={() => togglePick(entity)}
                        className="w-3 h-3 accent-[#22d3ee] cursor-pointer"
                        aria-label={`Select ${entity}`}
                      />
                      <span className={isPicked ? "text-[#22d3ee]" : ""}>{entity}</span>
                    </label>
                  </td>
                  {data.matrix[ri].map((v, ci) => {
                    const a = intensity(v);
                    const bg = a > 0 ? `rgba(34, 211, 238, ${a})` : "transparent";
                    return (
                      <td key={ci} className="text-center">
                        <button
                          type="button"
                          disabled={!v}
                          onClick={() => onCellClick(entity, data.cols[ci], v)}
                          data-testid={`heatmap-cell-${ri}-${ci}`}
                          title={v ? `${v} files in ${data.cols[ci].label} mention ${entity}` : "no overlap"}
                          className="block w-10 h-7 border border-white/5 disabled:cursor-default hover:outline hover:outline-1 hover:outline-[#22d3ee]"
                          style={{ background: bg, color: a > 0.5 ? "#050505" : "#a3a3a3" }}
                        >
                          {v || ""}
                        </button>
                      </td>
                    );
                  })}
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </Card>
  );
}


// ============================================================
// J — Mint Artifact Card
// ============================================================
function MintArtifactCard({ kind, artifactRef }) {
  const { user } = useAuth();
  const [state, setState] = useState({ loading: true, minted: false, edition: null });
  const [busy, setBusy] = useState(false);
  const [reward, setReward] = useState(null); // populated to trigger CardRewardModal

  useEffect(() => {
    let alive = true;
    if (!user) { setState({ loading: false, minted: false }); return; }
    api.get(`/artifact-cards/check/${kind}/${encodeURIComponent(artifactRef)}`)
      .then((r) => { if (alive) setState({ loading: false, ...r.data }); })
      .catch(() => { if (alive) setState({ loading: false, minted: false }); });
    return () => { alive = false; };
  }, [kind, artifactRef, user]);

  const mint = async () => {
    if (!user) {
      window.dispatchEvent(new CustomEvent("open-auth", { detail: { mode: "signup" } }));
      return;
    }
    if (state.minted || busy) return;
    setBusy(true);
    try {
      const r = await api.post("/artifact-cards/mint", { kind, ref: artifactRef });
      const card = r.data || {};
      setState({ loading: false, minted: true, edition: card?.edition });
      // Fire the cinematic vault-sealed reveal (same modal as daily pulls,
      // rolls, and puzzle prizes). The mint response carries everything
      // CardRewardModal needs to render the front face.
      setReward({
        card_id: card.canonical_id || card.id,
        card_def: {
          id: card.canonical_id || card.id,
          name: card.name,
          rarity: card.rarity,
          icon: "key",
          subject: "artifact",
          flavor: card.flavor || "",
          art_url: card.art_url || null,
          accent: card.accent || null,
        },
        card_name: card.name,
        card_rarity: card.rarity,
        edition_number: card.edition,
        edition_limit: null,
      });
    } catch (e) {
      const detail = e?.response?.data?.detail || "Mint failed";
      toast.error(detail);
    } finally { setBusy(false); }
  };

  return (
    <>
      {reward && (
        <CardRewardModal
          open
          reward={reward}
          source="artifact"
          onClose={() => setReward(null)}
        />
      )}
    <Card
      title="Mint as Vault Card"
      subtitle={
        <>
          Burn <strong className="text-[#ffd60a]">100 coins</strong> to mint a real collectible
          Vault Card from this file. The FIRST mint of this artifact rolls the rarity once and
          locks it forever — every future mint of the same file gets an identical-looking card,
          differing only by edition number.<br /><br />
          <span className="text-zinc-500">Rarity odds:</span>{" "}
          <strong className="text-[#22d3ee]">RARE 50%</strong>{" · "}
          <strong className="text-[#a78bfa]">MYTHIC 35%</strong>{" · "}
          <strong className="text-[#ff3b30]">CLASSIFIED 15%</strong>. Card art is generated by
          Gemini Nano Banana in the brutalist Vault style and lives forever on the public profile
          of every minter.
        </>
      }
      icon={<Coins size={11} />}
      accent="#ec4899"
      testid="mint-card"
    >
      {state.minted ? (
        <div data-testid="mint-already" className="inline-flex items-center gap-2 px-4 py-2 border border-[#00ff88]/50 text-[#00ff88] font-mono text-[11px] uppercase tracking-[0.2em]">
          <Check size={11} /> ALREADY MINTED · EDITION #{state.edition}
        </div>
      ) : (
        <button
          type="button"
          onClick={mint}
          disabled={busy}
          data-testid="mint-now-btn"
          className="inline-flex items-center gap-2 px-5 py-2.5 bg-[#ec4899] text-[#050505] hover:bg-white font-mono text-[11px] uppercase tracking-[0.2em] disabled:opacity-50 transition-colors"
        >
          {busy ? <><Loader2 size={11} className="animate-spin" /> MINTING…</> : <><Coins size={11} /> MINT FOR 100 COINS</>}
        </button>
      )}
    </Card>
    </>
  );
}

// ============================================================
// Tiny shared card chrome
// ============================================================
function Card({ title, subtitle, icon, accent, testid, children }) {
  return (
    <section className="border border-white/10 bg-[#0a0a0a]" data-testid={testid}>
      <header className="px-4 py-2.5 border-b border-white/10 flex items-center gap-2"
        style={{ color: accent }}>
        {icon}
        <span className="font-mono text-[10px] tracking-[0.25em] uppercase">// {title}</span>
      </header>
      <div className="p-4">
        {subtitle && (
          <p className="font-mono text-[10px] tracking-wider text-zinc-400 leading-relaxed mb-4 max-w-3xl">
            {subtitle}
          </p>
        )}
        {children}
      </div>
    </section>
  );
}
// ============================================================
